import React, { useCallback, useEffect } from "react";
import { makeStyles, ClickAwayListener, Typography, Box, Button } from "@material-ui/core";
import Link from "next/link";
import { useRouter } from "next/router";
import { animated, config, useTransition, useSpring } from "react-spring";

import ProductListItem from "../../product/ProductListItem";
import { formatPriceCents } from "../../product/utils";
import Tick from "../assets/tick.svg";
import useCart from "../../../hooks/useCart";
import YellowBullet from "../assets/yellowBullet.svg";
import GreenBullet from "../assets/greenBullet.svg";
import PinkBullet from "../assets/pinkBullet.svg";
import Basket from "../assets/basket.svg";
import useProductValidator from "hooks/useProductValidator";
import { pushCheckoutFunnelEnter, pushRemovedFromCart } from "lib/gtm";

export default function CartOverlayDesktop() {
  const classes = useStyle();
  const router = useRouter();
  const [cart, dispatch] = useCart();
  const [allCartItemsAreAvailable] = useProductValidator();
  const closeCart = useCallback(() => {
    dispatch({
      type: "close",
    });
  }, [dispatch]);

  const gotoCheckout = () => {
    pushCheckoutFunnelEnter(router.asPath);
    closeCart();
  };

  const deleteItem = (product) => {
    pushRemovedFromCart(product, "overlay");
    dispatch({ type: "remove", product });
  };

  useEffect(() => {
    router.events.on("routeChangeStart", closeCart);
    return () => {
      router.events.off("routeChangeStart", closeCart);
    };
  }, [router.events, closeCart]);

  const transitions = useTransition(cart.open ? cart.selection : [], (item) => item.product.slug, {
    unique: true,
    trail: 300,
    from: { opacity: 0, transform: "translateX(100%)" },
    enter: { opacity: 1, transform: "translateX(0px)" },
    leave: { opacity: 0, transform: "translateX(100%)" },
    config: config.default,
  });

  const style = useSpring({
    config: config.stiff,
    from: { transform: "translateY(-50%) translateX(45%) scale(0)", opacity: 0 },
    to: { transform: "translateY(0%) translateX(0%) scale(1)", opacity: 1 },
  });

  return (
    <ClickAwayListener onClickAway={closeCart}>
      <animated.div style={style} className={classes.container}>
        <Tick className={classes.tick} />
        <Typography variant="h2" align="center" className={classes.title}>
          Mon panier
        </Typography>
        <Box className={classes.itemsList}>
          {transitions.map(({ item: { product }, key, props }) => (
            <animated.div style={props} key={key}>
              <Box key={key} borderBottom="1px solid rgba(0, 0, 0, 0.10)" py={4}>
                <ProductListItem product={product} onDelete={() => deleteItem(product)} />
              </Box>
            </animated.div>
          ))}
        </Box>
        {cart.selection.length === 0 ? (
          <div className={classes.emptyContainer}>
            <div className={classes.emptyCartIcon}>
              <GreenBullet className={classes.greenBullet} />
              <YellowBullet className={classes.yellowBullet} />
              <PinkBullet className={classes.pinkBullet} />
              <Basket />
            </div>
            <Typography align="center" className={classes.emptyText}>
              Votre panier est vide
            </Typography>
          </div>
        ) : (
          <Box display="flex" justifyContent="space-between" my={4}>
            <Typography className={classes.bold}>Total</Typography>
            <Typography className={classes.bold}>
              {formatPriceCents(cart.selection.reduce((totalPrice, item) => totalPrice + item.product.price, 0))}
            </Typography>
          </Box>
        )}
        <Link disabled={cart.selection.length === 0} href="/reconditionne/panier" passHref>
          <Button
            disabled={cart.selection.length === 0 || !allCartItemsAreAvailable}
            fullWidth
            variant="contained"
            color="primary"
            onClick={gotoCheckout}
          >
            Passer commande
          </Button>
        </Link>
      </animated.div>
    </ClickAwayListener>
  );
}

const useStyle = makeStyles((theme) => ({
  container: {
    position: "absolute",
    width: "min(95vw , 400px)",
    padding: theme.spacing(2),
    right: -22,
    top: "150%",
    zIndex: 100,
    backgroundColor: "white",
    boxShadow: "0px 4px 76px rgba(0, 0, 0, 0.15)",
  },
  itemsList: {
    overflowY: "auto",
    overflowX: "hidden",
    maxHeight: "calc(100vh - 370px)",
  },
  emptyText: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(6),
    marginTop: theme.spacing(7),
  },
  emptyCartIcon: {
    borderRadius: 100,
    height: 200,
    width: 200,
    position: "relative",
    backgroundColor: "#F6F6F6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: theme.spacing(3),
  },
  pinkBullet: {
    position: "absolute",
    bottom: -4,
    right: 35,
  },
  greenBullet: {
    position: "absolute",
    bottom: "40%",
    left: -5,
  },
  yellowBullet: {
    position: "absolute",
    top: 5,
    right: 30,
  },
  tick: {
    position: "absolute",
    top: -9,
    right: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  bold: {
    fontWeight: 700,
  },
}));
